import { EnrichmentLogo } from '@src/components/shared/Logo';
import { Button } from '@src/components/ui/button';
import { getCognitoLogoutUrl } from '@src/config';
import { cn } from '@src/lib/utils';
import { resources } from '@src/resources';
import { CircleDollarSign, Clock, Database, LayoutGrid, LogOut, Menu, Rows3, Search, ZapOff } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: resources.navigation.overview, icon: LayoutGrid, path: '/' },
  { label: resources.navigation.runs, icon: Search, path: '/runs' },
  { label: resources.navigation.enrichments, icon: Database, path: '/enrichments' },
  { label: resources.navigation.cost, icon: CircleDollarSign, path: '/cost' },
  { label: resources.navigation.latency, icon: Clock, path: '/latency' },
  { label: resources.navigation.coverage, icon: Rows3, path: '/coverage' },
  { label: resources.navigation.circuitBreakers, icon: ZapOff, path: '/circuit-breakers' },
];

interface NavigationProps {
  children: ReactNode;
}

export function Navigation({ children }: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const user = auth.user?.profile;

  const activePath = location.pathname;

  // auth.removeUser() alone only clears this app's local oidc-client-ts session -- Cognito's
  // hosted-UI session cookie stays live, so a subsequent Sign in can silently re-auth without
  // prompting. When a hosted-UI domain is configured, redirect there to fully sign out of the
  // shared IdP session too; otherwise fall back to a local-only sign-out (see getCognitoLogoutUrl).
  const handleSignOut = async () => {
    const logoutUrl = getCognitoLogoutUrl();
    await auth.removeUser();
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    } else {
      console.warn(
        'Cognito hosted-UI domain is not configured (auth.cognitoDomain in /config.json) -- ' +
          'signed out of this app only, not the shared Cognito session.',
      );
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-foreground/50 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-56 flex-col gap-0.5 border-r border-sidebar-border bg-sidebar p-3 transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="p-2 pb-5">
          <EnrichmentLogo showText />
        </div>

        <nav className="flex-1 space-y-0.5">
          {navItems.map((item) => {
            const isActive = activePath === item.path;
            return (
              <Button
                key={item.path}
                type="button"
                variant="ghost"
                onClick={() => {
                  navigate(item.path);
                  setIsOpen(false);
                }}
                className={cn(
                  'h-auto w-full justify-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium',
                  isActive
                    ? 'bg-primary/10 text-sidebar-foreground hover:bg-primary/10'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
                )}
              >
                <item.icon className="h-[17px] w-[17px]" strokeWidth={1.6} />
                {item.label}
              </Button>
            );
          })}
        </nav>

        <div className="border-t border-sidebar-border p-2 pt-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => void handleSignOut()}
            className="h-auto w-full justify-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            <LogOut className="h-[17px] w-[17px]" strokeWidth={1.6} />
            {resources.navigation.logout}
          </Button>
          {user?.email !== undefined && (
            <p className="mt-1 truncate px-2.5 text-[10.5px] text-muted-foreground">{String(user.email)}</p>
          )}
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center border-b border-border bg-card px-4 lg:hidden">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            className="h-auto w-auto p-2 text-muted-foreground hover:bg-transparent hover:text-muted-foreground"
            onClick={() => setIsOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-9">
          <div className="mx-auto max-w-[1180px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
